import { Component, Injector, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
    AnnualReportMetaInformationDTO,
    AnnualReportNavigationProvider,
    AnnualReportProvider,
    getNextBalanceSheetDateForCompanyById,
    GetShareDto,
    onCreateAnnualReportSuccessfully,
} from '@modeso/aumico-lib-annualreport-fe';
import { AumGlobalAction, IAumGlobalActionEvent, ReportType } from '@modeso/aumico-lib-core-fe';
import { GetOrganizationDto, GetUserDto, IUserPreferences, OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { CPMMState } from '@modeso/aumico-lib-users-fe/enums/cpmm-state.enum';
import {
    AnnualReportStep,
    ISetupData,
    WorkflowConfigurationSelectionEnum,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ICompanyDTO, ICompanyNameIdLegalFormDTO } from '@modeso/types__aumico-lib-companies-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import {
    combineLatest,
    delay,
    distinctUntilChanged,
    filter,
    ignoreElements,
    map,
    mapTo,
    merge,
    mergeMap,
    of,
    pairwise,
    startWith,
    Subject,
    switchMap,
    take,
    takeUntil,
    tap,
    timer,
} from 'rxjs';
import { CompaniesProvider } from '../../../../../../aumico-lib-companies-fe/src/lib';
import { TasksOverviewState } from '../../../../../../aumico-lib-cpmm-fe/src/lib';
import { TasksOverviewActions } from '../../../../../../aumico-lib-cpmm-fe/src/lib/actions/tasks.actions';
import {
    setCompanySpecificProjectsFilter,
    setScrollToTextState,
    setSelectedTabState,
} from '../../../../../../aumico-lib-cpmm-fe/src/lib/actions/ui.actions';
import { SortDirection } from '../../../../../../aumico-lib-cpmm-fe/src/lib/enums/sort-direction.enum';
import { WorkflowArea } from '../../../../../../aumico-lib-cpmm-fe/src/lib/enums/workflow-area.enum';
import { selectTasksByProjectId } from '../../../../../../aumico-lib-cpmm-fe/src/lib/reducers/tasks.reducer';
import { selectCompanySpecificProjectsFilter } from '../../../../../../aumico-lib-cpmm-fe/src/lib/reducers/ui.reducer';
import {
    BulkDeleteDependencyWarning,
    ProjectOverviewTableValue,
    ProjectsOverviewActions,
    ProjectsOverviewState,
    selectProjectsOverview,
    selectTotalCount,
} from '../../../../../../aumico-lib-projects-fe/src/lib';
import { activeNavigation } from '../../../../../../aumico-lib-projects-fe/src/lib/enums/activeNavigation.enum';
import { ProjectSortingProperties } from '../../../../../../aumico-lib-projects-fe/src/lib/enums/project-sorting-properties.enum';
import { ProjectStatus } from '../../../../../../aumico-lib-projects-fe/src/lib/enums/project-status.enum';
import { ProjectsFilter } from '../../../../../../aumico-lib-projects-fe/src/lib/interfaces/projects-filter.interface';
import { MixpanelService } from '../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../mixpanel/mixpanelEvents.enum';
import { AssigneeFilter } from '../../../enums/assigneeFilter.enum';
import { Paths } from '../../../enums/paths.enum';
import { AnnualReportOrchestrator } from '../../../orchestrator/annualreport.orchestrator';
import { BasePageComponent } from '../../../pages/base.page';
import { ResetStoreService } from '../../../shared/services/resetStore.service';
import { ProjectsOverviewHelper } from '../../ms-annual-reports/helpers/projects-overview.helper';

const debug = Debug('aumico:company:ProjectsOverviewPage');

@UntilDestroy()
@Component({
    selector: 'app-page--company-projects',
    templateUrl: 'company-projects-overview.page.html',
    styleUrls: ['company-projects-overview.page.scss'],
})
export class CompanyProjectsOverviewPage extends BasePageComponent implements OnInit {
    viewOnlyMode = true;
    dialogRef: any;
    currentDialogIndex: number;
    shares: GetShareDto[];

    private shouldNavigateToEditPage = false;

    public userPreferredNumberOfDocumentsPerPage: number | undefined;
    private userPreferences: IUserPreferences | undefined;

    public showCreateProjectPopup = false;
    public companiesList: ICompanyNameIdLegalFormDTO[] = [];
    public company: ICompanyNameIdLegalFormDTO | undefined;
    public balanceSheetDate: Date | undefined;
    public selectedCompanyDto: ICompanyDTO | undefined;
    private reportType: ReportType | undefined;
    private pendingReportType: ReportType | undefined;
    public isCpmmActive: boolean = false;
    public isMandateAndTaskManagementEnabled: boolean = false;
    /** ABCP organisations see one Jahresabschluss column instead of the closing-workflow sections. */
    public abcpClosing = false;
    public mandateManagementWorkflow: WorkflowConfigurationSelectionEnum;
    public closingWorkflow: WorkflowConfigurationSelectionEnum;
    public isAutofillReport = false;
    activeProjectId: string;
    isCreatingAnnualReport: boolean;
    private currentCompanyId: string | undefined;

    statusOptions = [
        { label: 'Active', value: ProjectStatus.ACTIVE },
        { label: 'Done', value: ProjectStatus.DONE },
        { label: 'Archived', value: ProjectStatus.ARCHIVED },
    ];
    projectsFilter: ProjectsFilter | undefined = {};
    limit = 25;
    offset = 0;
    totalCount = 0;
    loadingMoreProjects = false;
    lockScrollTrigger = false;
    assigneeOptions: INameId[] = [];
    searchedAssigneeOptions: INameId[] = [];
    projectsOverviewTableData: ProjectOverviewTableValue[] = [];
    annualReportsMetaInformationRecords: AnnualReportMetaInformationDTO[] = [];
    activeMetaInformation: AnnualReportMetaInformationDTO | undefined;

    activeNavigation = activeNavigation.COMPANY;

    allProjectsSelected: boolean = false;
    selectedProjects: Set<string> = new Set<string>();

    // Variables for bulk edit
    bulkActions = [{ id: 'STATUS_CHANGE', translationKey: 'aumico_project_status_change_action_bar' }];
    deleteAction = { id: 'DELETE_PROJECTS', translationKey: 'aumico_project_delete_action_bar' };

    openBulkEditStatus: boolean = false;
    openBulkDeleteConfirmationDialog: boolean = false;
    openBulkDeleteDependencyWarningDialog: boolean = false;
    pendingDependencyWarnings: BulkDeleteDependencyWarning[] = [];
    pendingForceDeleteIds: string[] = [];
    possibleStatus = [
        {
            id: 'active',
            name: this.i18next.t('aumico_projects_status_active') as string,
        },
        {
            id: 'done',
            name: this.i18next.t('aumico_projects_status_done') as string,
        },
    ];
    aumico_cpmm_project_status_bulk_update_success_title;
    aumico_cpmm_project_status_bulk_update_success_description;
    bulkEditStatusControl = this.fb.control(null);
    openAutoTaskUpdateDialog = false;

    constructor(
        injector: Injector,
        dialog: MatDialog,
        protected override router: Router,
        private readonly annualReportProvider: AnnualReportProvider,
        private readonly annualReportNavigationProvider: AnnualReportNavigationProvider,
        private readonly resetStoreService: ResetStoreService,
        private readonly orchestratorOfAnnualReport: AnnualReportOrchestrator,
        private readonly mixpanelService: MixpanelService,
        private readonly companiesProvider: CompaniesProvider,
        private readonly organizationProvider: OrganizationProvider,
        private projectsOverviewStore: Store<ProjectsOverviewState>,
        private i18next: I18NextService,
        private store: Store<ProjectsOverviewState>,
        private tasksStore: Store<TasksOverviewState>,
        private fb: FormBuilder,
    ) {
        super(injector, dialog);

        this.annualReportProvider;

        this.authProvider
            .getCurrentUserRoles$()
            .pipe(filter((roles) => roles != null), untilDestroyed(this))
            .subscribe((roles: string[]) => {
                this.handleCurrentUserRolesPermissions(roles);
            });
    }

    override ngOnInit() {
        super.ngOnInit();

        // Subscribe to route params to handle navigation between different companies
        // This ensures the page updates when navigating from one company to another
        this.activatedRoute.params
            .pipe(
                map((params) => params['companyId']),
                distinctUntilChanged(),
                filter((companyId) => !!companyId),
                untilDestroyed(this),
            )
            .subscribe((companyId: string) => {
                const isCompanyChange = this.currentCompanyId && this.currentCompanyId !== companyId;
                this.currentCompanyId = companyId;

                // If company changed, reset UI state
                if (isCompanyChange) {
                    this.clearAllSelections();
                    this.offset = 0;
                    this.limit = 25;
                    this.projectsOverviewTableData = [];
                }

                // Fetch company data
                this.companiesProvider.dispatchCompanyById(companyId);

                // Update filter and fetch projects
                this.projectsFilter = {
                    ...this.projectsFilter,
                    companyIds: [companyId],
                    offset: this.offset,
                    limit: this.limit,
                };
                this.projectsOverviewStore.dispatch(
                    ProjectsOverviewActions.loadProjectsOverview({ filter: this.projectsFilter }),
                );
            });

        this.resetNeededStores();

        this.userPreferences = this.localStorageService.getUserPrefrences();
        this.userPreferredNumberOfDocumentsPerPage = this.userPreferences?.numberOfDocumentsPerPage;

        this.organizationProvider.dispatchGetUsers(true);

        this.annualReportProvider.dispatchActiveAnnualReportsList();

        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(
                filter((organization) => organization != null),
                take(1),
                untilDestroyed(this),
            )
            .subscribe((organization: GetOrganizationDto) => {
                this.isCpmmActive = organization.cpmmActivationDetails?.activationState === CPMMState.ACTIVE;
                this.isMandateAndTaskManagementEnabled = organization.isMandateAndTaskManagementEnabled ?? false;
                this.abcpClosing = organization.abcpPreview === true;
            });

        this.actionsListener$
            .pipe(
                ofType(getNextBalanceSheetDateForCompanyById.getNextBalanceSheetDateForCompanyByIdSuccess),
                filter((payload) => payload?.payload != null),
                untilDestroyed(this),
            )
            .subscribe((payload) => {
                const dateString = payload.payload; // This is a string

                // Parse the date string as UTC to extract year, month, day without timezone interference
                const utcDate = new Date(dateString);
                this.balanceSheetDate = new Date(
                    Date.UTC(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate(), 0, 0, 0, 0),
                );

                this.mandateManagementWorkflow =
                    WorkflowConfigurationSelectionEnum.COPY_CONFIGURATION_FROM_PREVIOUS_YEAR;
                this.closingWorkflow = WorkflowConfigurationSelectionEnum.COPY_CONFIGURATION_FROM_PREVIOUS_YEAR;
            });

        this.actionsListener$
            .pipe(
                ofType(onCreateAnnualReportSuccessfully),

                //   setActiveProjectId
                tap(({ payload }) => {
                    this.store.dispatch(
                        ProjectsOverviewActions.setActiveProjectId({
                            projectId: payload.projectId,
                        }),
                    );
                    this.activeProjectId = payload.projectId;
                }),

                switchMap(({ payload }) => {
                    if (!this.isCpmmActive || !this.isMandateAndTaskManagementEnabled) {
                        // skip all task-loading logic
                        return of(payload);
                    }

                    const stopPolling$ = new Subject<void>();

                    // polling stream: dispatch loadProjectTasks every 1s until stopPolling$
                    const poll$ = timer(3000, 1000).pipe(
                        takeUntil(stopPolling$),
                        tap(() =>
                            this.tasksStore.dispatch(
                                TasksOverviewActions.loadProjectTasks({
                                    projectId: this.activeProjectId,
                                    filter: { offset: 0, limit: 1000 },
                                }),
                            ),
                        ),
                        ignoreElements(),
                    );

                    // stabilization watcher: watch tasks$.length → when two in a row match
                    const stable$ = this.store.select(selectTasksByProjectId(this.activeProjectId)).pipe(
                        map((list) => list.length),
                        startWith(-1),
                        pairwise(),
                        filter(([prev, curr]) => prev === curr),
                        take(1),
                        tap(() => {
                            stopPolling$.next();
                            stopPolling$.complete();
                        }),
                        mapTo(payload), // emit the payload through
                    );

                    // merge and only take one emission (the payload once stable)
                    return merge(poll$, stable$).pipe(take(1));
                }),

                //  stop showing "creating" UI
                tap(() => (this.isCreatingAnnualReport = false)),

                switchMap((payload) =>
                    // first set it
                    of(null).pipe(
                        tap(() => {
                            this.annualReportProvider.setActiveAnnualReportEncryptedId(payload.annualReportEncryptedId);
                        }),
                        // then fetch the encryptedId back
                        switchMap(() => this.annualReportProvider.getActiveAnnualReportEncryptedId$()),
                    ),
                ),

                untilDestroyed(this),
            )
            .subscribe((encryptedId) => {
                this.shouldNavigateToEditPage = true;
                this.orchestratorOfAnnualReport.load(encryptedId);
            });

        this.companiesProvider
            .getSelectedCompanyById$()
            .pipe(
                filter((company) => company != null),
                untilDestroyed(this),
            )
            .subscribe((company: ICompanyDTO) => {
                this.company = {
                    _id: company._id,
                    name: company.name,
                    legalform: company.legalform,
                };

                this.selectedCompanyDto = company;
                if (this.pendingReportType) {
                    this.dispatchNextBalanceSheetDateForCompany(this.pendingReportType);
                }
            });

        this.companiesProvider
            .getCompaniesNameIdLegalForm$()
            .pipe(untilDestroyed(this))
            .subscribe((companies) => {
                this.companiesList = companies;
            });

        this.companiesProvider.dispatchCompaniesNameIdLegalFormList();

        this.annualReportProvider
            .getAnnualReports$()
            .pipe(untilDestroyed(this))
            .subscribe((annualReportsMetaInformationRecords) => {
                this.annualReportsMetaInformationRecords = cloneDeep<AnnualReportMetaInformationDTO[]>(
                    annualReportsMetaInformationRecords,
                );
            });

        this.annualReportProvider
            .getActiveMetaInformation$()
            .pipe(
                filter((annualReport) => annualReport != null),

                switchMap((metaInfo: AnnualReportMetaInformationDTO) =>
                    of(metaInfo).pipe(
                        filter((annualReportMetaInfo) => annualReportMetaInfo != null),
                        delay(1000), // ⏱️ wait 1 second for prject meta information record to be created
                        tap(() => {
                            this.activeMetaInformation = cloneDeep(metaInfo);
                            this.projectsOverviewStore.dispatch(
                                ProjectsOverviewActions.fetchProjectIdByAnnualReportMetaInfoId({
                                    annualReportMetaId: metaInfo._id,
                                }),
                            );
                        }),
                    ),
                ),
                untilDestroyed(this),
            )
            .subscribe({});

        this.annualReportProvider
            .getActiveAnnualReport$()
            .pipe(
                filter((annualReport) => annualReport != null),
                mergeMap((annualReport) =>
                    this.annualReportProvider
                        .getActiveMetaInformation$()
                        .pipe(map((metaInformation) => ({ annualReport, metaInformation }))),
                ),
                untilDestroyed(this),
            )
            .subscribe(({ annualReport, metaInformation }) => {
                const urlParts = this.router.url.split('/');
                urlParts.pop();
                urlParts.pop();
                urlParts.pop();
                const newUrl = urlParts.join('/');
                const backroute = this.router.url;

                const isInterimReport =
                    annualReport.setupData?.reportType === ReportType.QUARTERLY ||
                    annualReport.setupData?.reportType === ReportType.MONTHLY ||
                    annualReport.setupData?.reportType === ReportType.CUSTOM;

                // Navigate to edit page - activeProjectId should now be properly set
                // This is used when we create a new project
                if (
                    this.shouldNavigateToEditPage &&
                    !(metaInformation && metaInformation.isSealed) &&
                    (!this.isCpmmActive || !this.isMandateAndTaskManagementEnabled || isInterimReport)
                ) {
                    this.router.navigateByUrl(
                        `${newUrl}/project/${this.activeProjectId}?backTarget=${encodeURIComponent(backroute)}`,
                    );
                } else if (
                    this.shouldNavigateToEditPage &&
                    !(metaInformation && metaInformation.isSealed) &&
                    this.isCpmmActive && this.isMandateAndTaskManagementEnabled &&
                    !isInterimReport
                ) {
                    this.router.navigateByUrl(
                        `${newUrl}/project/${this.activeProjectId}/cpmm?backTarget=${encodeURIComponent(backroute)}`,
                    );
                } else if (this.shouldNavigateToEditPage && metaInformation.isSealed) {
                    this.annualReportNavigationProvider.setActiveArNavigation([AnnualReportStep.PRINT], true);

                    if (this.isCpmmActive && this.isMandateAndTaskManagementEnabled) {
                        this.router.navigateByUrl(
                            `${newUrl}/project/${this.activeProjectId}/cpmm/report/${Paths.PRINT
                            }?backTarget=${encodeURIComponent(backroute)}`,
                        );
                    } else {
                        this.router.navigateByUrl(
                            `${newUrl}/project/${this.activeProjectId}/${Paths.PRINT}?backTarget=${encodeURIComponent(
                                backroute,
                            )}`,
                        );
                    }
                }
            });

        combineLatest([
            this.projectsOverviewStore.select(selectProjectsOverview),
            this.projectsOverviewStore.select(selectTotalCount),
            this.activeUserAumicoInternalId$,
        ])
            .pipe(
                switchMap(([projectsMetaInformation, totalCount, activeUserId]) =>
                    this.organizationProvider.getUsers().pipe(
                        filter((users) => Object.keys(users).length > 0), // wait until users are loaded
                        map((users: GetUserDto[]) => {
                            // Create userMap with ALL users (including deleted) for display lookups
                            const userMap = new Map<string, GetUserDto>();
                            users.forEach(user => userMap.set(user.id, user));

                            // Build assigneeOptions using only ACTIVE users for dropdowns
                            this.assigneeOptions = [];

                            const userOptions = users
                                .filter(user => !user.isDeleted && !user.isInvitee)
                                .map((user) => ({
                                    id: user.id,
                                    name: user.firstName + ' ' + user.lastName,
                                    isMe: user.isMe,
                                }));

                            userOptions.unshift({
                                id: AssigneeFilter.unassigned,
                                name: this.i18next.t('aumico_tasks_unassigned') as string,
                                isMe: false,
                            });

                            // Add current user to the top of the list
                            const index = userOptions.findIndex((user) => user.isMe === true);
                            if (index > -1) {
                                const [item] = userOptions.splice(index, 1); // Remove the item
                                userOptions.unshift(item); // Add it to the beginning
                            }

                            this.assigneeOptions.push(...userOptions); // Add user options to the list
                            this.searchedAssigneeOptions = cloneDeep(this.assigneeOptions);

                            this.totalCount = totalCount;

                            const updated: ProjectOverviewTableValue[] = cloneDeep(
                                projectsMetaInformation.map((project) => {
                                    const assignedUser = userMap.get(project.assignee);
                                    const clerkUser = userMap.get(project.clerk);
                                    return {
                                        ...project,
                                        assignee: assignedUser
                                            ? `${assignedUser.firstName} ${assignedUser.lastName}`
                                            : (this.i18next.t('aumico_tasks_unassigned') as string),
                                        assigneeDeleted: assignedUser?.isDeleted ?? false,
                                        clerk: clerkUser
                                            ? `${clerkUser.firstName} ${clerkUser.lastName}`
                                            : (this.i18next.t('aumico_tasks_unassigned') as string),
                                        clerkDeleted: clerkUser?.isDeleted ?? false,
                                    };
                                }),
                            );

                            return updated;
                        }),
                    ),
                ),
                untilDestroyed(this),
                // Adjust workflowData based on isCpmmActive
                map((records: ProjectOverviewTableValue[]) =>
                    records.map((record) => ({
                        ...record,
                        workflowData: this.isCpmmActive && this.isMandateAndTaskManagementEnabled ? record.workflowData : null,
                    })),
                ),
            )
            .subscribe((projectsMetaInformationRecords) => {
                this.loadingMoreProjects = false;

                // Release scroll lock AFTER rendering
                setTimeout(() => {
                    this.lockScrollTrigger = false;
                }, 300); // delay ensures scrollHeight is updated

                // Store previous state for smart cleanup
                const previousProjectIds = new Set<string>();
                if (this.projectsOverviewTableData.length > 0) {
                    // Collect all current project IDs
                    this.projectsOverviewTableData.forEach((project) => {
                        previousProjectIds.add(project.projectId);
                    });
                }

                // Update the data
                this.projectsOverviewTableData = cloneDeep(projectsMetaInformationRecords);

                // Smart cleanup: Remove selections for projects no longer visible after filtering
                if (previousProjectIds.size > 0 && (this.selectedProjects.size > 0 || this.allProjectsSelected)) {
                    this.cleanupInvalidSelections();
                }
            });

        this.actionsListener$
            .pipe(ofType(ProjectsOverviewActions.projectStatusUpdated), untilDestroyed(this))
            .subscribe(() => {
                this.fireSuccessNotification({
                    descriptionKey: this.i18next.t('aumico_project_overview_status_update_success_message') as string,
                });
            });

        this.actionsListener$
            .pipe(ofType(ProjectsOverviewActions.projectStatusUpdateFailure), untilDestroyed(this))
            .subscribe(() => {
                this.fireErrorMessage(this.i18next.t('aumico_project_overview_status_update_error_message') as string);
            });

        this.actionsListener$
            .pipe(ofType(ProjectsOverviewActions.projectDeleted), untilDestroyed(this))
            .subscribe(() => {
                this.refetchProjects(false);
                this.fireSuccessNotification({
                    descriptionKey: this.i18next.t('aumico_project_overview_project_delete_success_message') as string,
                });
            });

        this.actionsListener$
            .pipe(ofType(ProjectsOverviewActions.projectDeleteFailure), untilDestroyed(this))
            .subscribe((payload) => {
                const message: string = payload.error?.error?.message ?? '';
                if (message.includes('aumico_backend_errors_project_deletion_due_to_dependency') && payload.projectId) {
                    // Asks whether to delete the project although other reports build on it.
                    const reportsStr = message.split(':').slice(1).join(':').trim();
                    const dependentReports = reportsStr ? reportsStr.split(', ').filter(Boolean) : [];
                    this.pendingDependencyWarnings = [{ projectId: payload.projectId, dependentReports }];
                    this.pendingForceDeleteIds = [payload.projectId];
                    this.openBulkDeleteDependencyWarningDialog = true;
                } else {
                    this.fireErrorMessage(
                        this.i18next.t('aumico_project_overview_project_delete_error_message') as string,
                    );
                }
            });

        // Asks whether to delete projects that other reports build on.
        this.actionsListener$
            .pipe(ofType(ProjectsOverviewActions.bulkProjectsDeleted), untilDestroyed(this))
            .subscribe(({ warnings }) => {
                const safeWarnings = Array.isArray(warnings) ? warnings : [];
                this.pendingDependencyWarnings = safeWarnings;
                this.openBulkDeleteDependencyWarningDialog = safeWarnings.length > 0;
                this.pendingForceDeleteIds = safeWarnings.map((w) => w.projectId);
            });
        this.store
            .select(selectCompanySpecificProjectsFilter())
            .pipe(untilDestroyed(this))
            .subscribe((projectsFilter) => {
                this.projectsFilter = cloneDeep({
                    ...projectsFilter,
                    companyIds: this.projectsFilter?.companyIds || [],
                    // No serachfilter in company projects overview -> set companyName to be emoty string -> Finds all projects
                    companyName: '',
                });
                this.refetchProjects(false); // Do not dispatch the filter again
            });
    }

    private handleCurrentUserRolesPermissions(roles: string[]): void {
        if (roles.includes('ORG_USER') || roles.includes('ORG_ADMIN')) {
            this.viewOnlyMode = false;
            return;
        }
        this.viewOnlyMode = true;
    }

    createAnnualReport(setupData: ISetupData) {
        debug('create new annual report');
        this.mixpanelService.track(MIXPANEL_EVENTS.CREATE_ANNNUAL_REPORT_BUTTON_CLICK);
        this.orchestratorOfAnnualReport.new(setupData);

        const event: IAumGlobalActionEvent = {
            action: AumGlobalAction.UPDATECURRENTENTITY,
            value: 'New',
        };
        this.actionHeaderService.onAction(event);
        this.isCreatingAnnualReport = true;
    }

    onSelectProject(selection: {
        project: ProjectOverviewTableValue;
        area?: WorkflowArea | 'report' | 'closing';
        section?: string;
    }): void {
        const annualReportMeta = this.annualReportsMetaInformationRecords.find(
            (arMeta) => arMeta._id === selection.project.annualReportMetaData,
        );

        if (annualReportMeta && annualReportMeta.annualReportEncryptedId) {
            this.annualReportProvider.setActiveAnnualReportEncryptedId(annualReportMeta.annualReportEncryptedId);
            this.orchestratorOfAnnualReport.load(annualReportMeta.annualReportEncryptedId);
        }

        this.shouldNavigateToEditPage = true;

        // We should not load the data here, instead it shall be loaded on the fs edit page
        // Here we should simply navigate to mandate management page
        this.store.dispatch(ProjectsOverviewActions.setActiveProjectId({ projectId: selection.project.projectId }));
        this.store.dispatch(
            ProjectsOverviewActions.fetchProjectDataByProjectId({
                projectId: selection.project.projectId,
            }),
        );
        if (selection.area && selection.area === WorkflowArea.MANDATE_MANAGEMENT && selection.section) {
            this.store.dispatch(setScrollToTextState({ text: selection.section }));
        }
        if (selection.area && selection.area === WorkflowArea.CLOSING_PROCESS && selection.section) {
            this.store.dispatch(
                setSelectedTabState({
                    locationId: selection.project.projectId + '__' + selection.area,
                    selectedTabKey: selection.section,
                }),
            );
        }
        // Navigate to edit page
        const urlParts = this.router.url.split('/');
        urlParts.pop();
        urlParts.pop();
        urlParts.pop();
        const newUrl = urlParts.join('/');
        const backroute = this.router.url;

        const isInterimReport =
            selection.project.reportType === ReportType.QUARTERLY ||
            selection.project.reportType === ReportType.MONTHLY ||
            selection.project.reportType === ReportType.CUSTOM;

        let reportSubpath = `/${Paths.GENERAL_INFORMATION}`;
        if (annualReportMeta && annualReportMeta.isSealed) {
            this.annualReportNavigationProvider.setActiveArNavigation([AnnualReportStep.PRINT], true);
            reportSubpath = `/${Paths.PRINT}`;
        }

        if (selection.area === 'report' || isInterimReport) {
            if (this.isCpmmActive && this.isMandateAndTaskManagementEnabled && !isInterimReport) {
                this.router.navigateByUrl(
                    `${newUrl}/project/${selection.project.projectId
                    }/cpmm/report${reportSubpath}?backTarget=${encodeURIComponent(backroute)}`,
                );
            } else {
                this.router.navigateByUrl(
                    `${newUrl}/project/${selection.project.projectId}${reportSubpath}?backTarget=${encodeURIComponent(
                        backroute,
                    )}`,
                );
            }

            return;
        }
        // The closing's own gate decides between the setup wizard and the overview.
        if (selection.area === 'closing') {
            this.router.navigateByUrl(
                `${newUrl}/project/${selection.project.projectId}/abcp/${Paths.CLOSING}?backTarget=${encodeURIComponent(
                    backroute,
                )}`,
            );
            return;
        }
        if (selection.area === WorkflowArea.CLOSING_PROCESS) {
            this.router.navigateByUrl(
                `${newUrl}/project/${selection.project.projectId}/cpmm/${Paths.CLOSING_WORKFLOW
                }?backTarget=${encodeURIComponent(backroute)}`,
            );
            return;
        }
        if (selection.area === WorkflowArea.MANDATE_MANAGEMENT) {
            this.router.navigateByUrl(
                `${newUrl}/project/${selection.project.projectId}/cpmm/${Paths.MANDATE_MANAGEMENT
                }?backTarget=${encodeURIComponent(backroute)}`,
            );
            return;
        }

        if (!(annualReportMeta && annualReportMeta.isSealed) && (!this.isCpmmActive || !this.isMandateAndTaskManagementEnabled || isInterimReport)) {
            this.router.navigateByUrl(
                `${newUrl}/project/${selection.project.projectId}?backTarget=${encodeURIComponent(backroute)}`,
            );
        } else if (!(annualReportMeta && annualReportMeta.isSealed) && this.isCpmmActive && this.isMandateAndTaskManagementEnabled && !isInterimReport) {
            this.router.navigateByUrl(
                `${newUrl}/project/${selection.project.projectId}/cpmm?backTarget=${encodeURIComponent(backroute)}`,
            );
        } else if (this.shouldNavigateToEditPage && annualReportMeta.isSealed) {
            this.annualReportNavigationProvider.setActiveArNavigation([AnnualReportStep.PRINT], true);
            if (this.isCpmmActive && this.isMandateAndTaskManagementEnabled) {
                this.router.navigateByUrl(
                    `${newUrl}/project/${selection.project.projectId}/cpmm/report/${Paths.PRINT
                    }?backTarget=${encodeURIComponent(backroute)}`,
                );
            } else {
                this.router.navigateByUrl(
                    `${newUrl}/project/${selection.project.projectId}/${Paths.PRINT}?backTarget=${encodeURIComponent(
                        backroute,
                    )}`,
                );
            }
        }
    }
    private resetNeededStores() {
        this.resetStoreService.resetTemplates();
        this.resetStoreService.resetNotes();
        this.resetStoreService.resetAnnualReportNavigation();
        this.resetStoreService.resetProjects();
        this.resetStoreService.resetAnnualReports();
    }

    public onNumberOfDocumentsPerPageChanged(numberOfDocumentsPerPage: number): void {
        this.userPreferredNumberOfDocumentsPerPage = numberOfDocumentsPerPage;
        this.authProvider.dispatchUpdateUserPreferences({
            ...this.userPreferences,
            numberOfDocumentsPerPage: numberOfDocumentsPerPage,
        });
        this.localStorageService.setUserPrefrences({
            ...this.userPreferences,
            numberOfDocumentsPerPage: numberOfDocumentsPerPage,
        });
    }

    public onCompanySelected(company: ICompanyNameIdLegalFormDTO): void {
        this.companiesProvider.dispatchCompanyById(company._id);
    }

    public onReportTypeSelected(reportType: ReportType): void {
        this.reportType = reportType;
        this.dispatchNextBalanceSheetDateForCompany(reportType);
    }

    private dispatchNextBalanceSheetDateForCompany(type: ReportType): void {
        const companyId = this.selectedCompanyDto?._id;
        if (!companyId) {
            this.pendingReportType = type;
            debug('Delaying balance sheet date lookup until the company selection is loaded.');
            return;
        }

        this.pendingReportType = undefined;
        this.annualReportProvider.dispatchGetNextBalanceSheetDateForCompanyById(companyId, type);
    }

    public onBalanceSheetDateSelected(balanceSheetDate: Date): void {
        this.balanceSheetDate = balanceSheetDate;
    }

    public onMandateManagementWorkflowSelected(mandateManagementWorkflow: WorkflowConfigurationSelectionEnum): void {
        this.mandateManagementWorkflow = mandateManagementWorkflow;
    }

    public onClosingWorkflowSelected(closingWorkflow: WorkflowConfigurationSelectionEnum): void {
        this.closingWorkflow = closingWorkflow;
    }

    public closeNewProjectDialog(closeAndSave: boolean): void {
        this.showCreateProjectPopup = false;
        if (closeAndSave) {
            const setupData: ISetupData = {
                company: this.selectedCompanyDto,
                balanceSheetDate: this.balanceSheetDate.toISOString(),
                mandateManagementWorkflow:
                    this.mandateManagementWorkflow ??
                    WorkflowConfigurationSelectionEnum.RESET_TO_STANDARD_CONFIGURATION,
                closingWorkflow:
                    this.closingWorkflow ?? WorkflowConfigurationSelectionEnum.RESET_TO_STANDARD_CONFIGURATION,
                reportType: this.reportType ?? ReportType.ANNUAL,
                fxEnabled: this.selectedCompanyDto?.fx?.enabled === true,
            };
            this.createAnnualReport(setupData);
        }
    }

    public openCreateProjectDialog(): void {
        this.showCreateProjectPopup = true;
    }

    loadMoreProjects(): void {
        if (this.offset >= this.totalCount || this.totalCount < this.limit) {
            this.lockScrollTrigger = false; // release lock even if skipped
            return; //  prevent redundant load
        }

        this.loadingMoreProjects = true;
        this.offset += this.limit;
        this.projectsFilter = ProjectsOverviewHelper.buildProjectsFilter(
            this.projectsFilter,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            this.offset,
            this.limit,
        );
        this.projectsOverviewStore.dispatch(ProjectsOverviewActions.loadMoreProjects({ filter: this.projectsFilter }));
    }

    sortProjects(sortEvent: { sortBy: ProjectSortingProperties | null; sortOrder: SortDirection | null }): void {
        if (sortEvent.sortBy === null || sortEvent.sortOrder === null) {
            this.projectsFilter = {
                ...this.projectsFilter,
                sortBy: undefined,
                sortOrder: undefined,
            };
        } else {
            this.projectsFilter = ProjectsOverviewHelper.buildProjectsFilter(
                this.projectsFilter,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                sortEvent.sortBy,
                sortEvent.sortOrder,
            );
        }
        this.refetchProjects();
    }

    onDueDateFilterChange(years: number[]) {
        if (years.length === 0) {
            this.projectsFilter = {
                ...this.projectsFilter,
                years: undefined,
                startDate: undefined,
                endDate: undefined,
            };
        } else {
            // Clear start and end date if years are selected
            this.projectsFilter = {
                ...this.projectsFilter,
                startDate: undefined,
                endDate: undefined,
            };
            this.projectsFilter = ProjectsOverviewHelper.buildProjectsFilter(
                this.projectsFilter,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                years,
            );
        }
        this.refetchProjects();
    }

    onAssigneeFilterChange(userIds: string[]): void {
        this.projectsFilter = ProjectsOverviewHelper.buildProjectsFilter(
            this.projectsFilter,
            undefined,
            undefined,
            userIds,
        );

        this.refetchProjects();
    }

    onSearchFilterChangeAssignee(searchTerm: string): void {
        if (searchTerm === '' || searchTerm === null) {
            this.searchedAssigneeOptions = this.assigneeOptions;
            return;
        }
        this.searchedAssigneeOptions = this.assigneeOptions.filter((assignee) =>
            assignee.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }

    onProjectStatusFilterChange(projectStatus: ProjectStatus[]): void {
        if (projectStatus.length === 0) {
            this.projectsFilter = {
                ...this.projectsFilter,
                projectStatus: undefined,
            };
        }
        this.projectsFilter = ProjectsOverviewHelper.buildProjectsFilter(
            this.projectsFilter,
            undefined,
            undefined,
            undefined,
            projectStatus,
        );
        this.refetchProjects();
    }

    onReportTypeFilterChange(reportTypes: ReportType[]): void {
        this.projectsFilter = ProjectsOverviewHelper.buildProjectsFilter(
            this.projectsFilter,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            reportTypes,
        );
        this.refetchProjects();
    }

    onSearchFilterChange(searchTerm: string): void {
        if (searchTerm === null) {
            searchTerm = '';
        }
        this.projectsFilter = ProjectsOverviewHelper.buildProjectsFilter(
            this.projectsFilter,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            searchTerm,
        );
        this.refetchProjects();
    }

    refetchProjects(dispatch: boolean = true): void {
        this.limit = this.limit + this.offset; // Update limit to include already loaded tasks
        this.offset = 0; // Reset offset for new filter
        this.projectsFilter = {
            ...this.projectsFilter,
            limit: this.limit,
            offset: this.offset,
        };
        if (dispatch) {
            this.store.dispatch(setCompanySpecificProjectsFilter({ filter: this.projectsFilter }));
        }
        this.projectsOverviewStore.dispatch(
            ProjectsOverviewActions.loadProjectsOverview({ filter: this.projectsFilter }),
        );
    }

    onUpdateProjectStatus(data: { projectId: string; status: ProjectStatus; updateTasks: boolean }): void {
        // Loop over workflowData
        // For mandate Management Tasks we use issueId and make an array to use bulkupdate for them
        // For Closing Process we can also use issueId, but here its a section, so we update for the full section directly

        // Get Project Data by its Id
        const projectData = this.projectsOverviewTableData.find((item) => item.projectId === data.projectId);

        if (!projectData.workflowData || !this.isCpmmActive || !this.isMandateAndTaskManagementEnabled) {
            this.store.dispatch(ProjectsOverviewActions.updateProjectStatus(data));
            return;
        }

        let mandateManagementTaskIds: string[] = [];
        let closingSectionIds: string[] = [];
        for (const item of projectData.workflowData) {
            if (item.area === WorkflowArea.MANDATE_MANAGEMENT) {
                for (const task of item.issuesDetails) {
                    mandateManagementTaskIds.push(task.issueId);
                }
            } else if (item.area === WorkflowArea.CLOSING_PROCESS) {
                for (const section of item.issuesDetails) {
                    closingSectionIds.push(section.issueId);
                }
            }
        }
        if (data.status === ProjectStatus.DONE) {
            const enrichedData = {
                ...data,
                mandateManagementTaskIds,
                closingSectionIds,
            };
            this.store.dispatch(ProjectsOverviewActions.updateProjectStatus(enrichedData));
        } else {
            this.store.dispatch(ProjectsOverviewActions.updateProjectStatus(data));
        }
    }

    onDeleteProject(projectId: string): void {
        this.projectsOverviewStore.dispatch(ProjectsOverviewActions.deleteProject({ projectId }));
    }

    public isAnyFilterActive(): boolean {
        return !!(
            this.projectsFilter.startDate ||
            this.projectsFilter.endDate ||
            this.projectsFilter.years?.length ||
            this.projectsFilter.assignee ||
            this.projectsFilter.projectStatus
        );
    }

    // Selection logic methods
    clearAllSelections(): void {
        this.selectedProjects.clear();
        this.allProjectsSelected = false;
    }

    onToggleCheckAllProject(): void {
        if (this.allProjectsSelected) {
            this.clearAllSelections();
        } else {
            this.selectedProjects.clear();

            this.projectsOverviewTableData.forEach((project) => {
                this.selectedProjects.add(project.projectId);
            });
            this.allProjectsSelected = true;
        }
    }

    onToggleCheckProject(projectId: string): void {
        if (this.selectedProjects.has(projectId)) {
            this.selectedProjects.delete(projectId);
            this.allProjectsSelected = false;
        } else {
            this.selectedProjects.add(projectId);
        }
    }

    onActionClicked(actionId: string): void {
        if (actionId === 'STATUS_CHANGE') {
            this.openBulkEditStatus = true;
        } else if (actionId === 'DELETE_PROJECTS') {
            // Action to delete selected projects
            this.openBulkDeleteConfirmationDialog = true;
        } else if (actionId === 'CLOSE_BULK_EDIT') {
            this.clearAllSelections();
        }
    }

    confirmDeleteBulkProjects(): void {
        this.openBulkDeleteConfirmationDialog = false;
        this.store.dispatch(
            ProjectsOverviewActions.bulkDeleteProjects({
                projectIds: Array.from(this.selectedProjects),
                filter: this.projectsFilter,
            }),
        );
        this.clearAllSelections();
    }

    confirmDeleteWithDependencies(): void {
        this.openBulkDeleteDependencyWarningDialog = false;
        this.store.dispatch(
            ProjectsOverviewActions.confirmBulkDeleteWithDependencies({
                projectIds: this.pendingForceDeleteIds,
                filter: this.projectsFilter,
            }),
        );
        this.store.dispatch(ProjectsOverviewActions.clearBulkDeleteWarnings());
        this.resetBulkDeleteWarningState();
    }

    skipDeleteWithDependencies(): void {
        this.openBulkDeleteDependencyWarningDialog = false;
        this.store.dispatch(ProjectsOverviewActions.clearBulkDeleteWarnings());
        this.resetBulkDeleteWarningState();
    }

    getProjectNameById(projectId: string): string {
        return this.projectsOverviewTableData.find((p) => p.projectId === projectId)?.projectName ?? projectId;
    }

    private resetBulkDeleteWarningState(): void {
        this.pendingForceDeleteIds = [];
        this.pendingDependencyWarnings = [];
    }

    cancelDeleteBulkProjects(): void {
        this.openBulkDeleteConfirmationDialog = false;
    }

    editStatusSelected(): void {
        if (this.bulkEditStatusControl.value) {
            this.openBulkEditStatus = false;

            if (this.isCpmmActive && this.isMandateAndTaskManagementEnabled && this.bulkEditStatusControl.value.id === ProjectStatus.DONE) {
                this.openAutoTaskUpdateDialog = true;
            } else {
                this.onEditStatusConfirm(false);
            }
        }
    }

    onEditStatusConfirm(updateTasks: boolean): void {
        this.openAutoTaskUpdateDialog = false;

        if (this.bulkEditStatusControl.value) {
            this.openBulkEditStatus = false;

            let mandateManagementTaskIds: string[] = [];
            let closingSectionIds: string[] = [];
            if (updateTasks && this.isCpmmActive && this.isMandateAndTaskManagementEnabled) {
                this.selectedProjects.forEach((projectId) => {
                    // Get Project Data by its Id
                    const projectData = this.projectsOverviewTableData.find((item) => item.projectId === projectId);

                    for (const item of projectData?.workflowData) {
                        if (item.area === WorkflowArea.MANDATE_MANAGEMENT) {
                            for (const task of item.issuesDetails) {
                                mandateManagementTaskIds.push(task.issueId);
                            }
                        } else if (item.area === WorkflowArea.CLOSING_PROCESS) {
                            for (const section of item.issuesDetails) {
                                closingSectionIds.push(section.issueId);
                            }
                        }
                    }
                });
            }
            if (this.bulkEditStatusControl.value === ProjectStatus.DONE) {
                this.store.dispatch(
                    ProjectsOverviewActions.bulkUpdateProjectStatus({
                        projectIds: Array.from(this.selectedProjects),
                        status: this.bulkEditStatusControl.value.id,
                        updateTasks: updateTasks,
                        mandateManagementTaskIds,
                        closingSectionIds,
                    }),
                );
            } else {
                this.store.dispatch(
                    ProjectsOverviewActions.bulkUpdateProjectStatus({
                        projectIds: Array.from(this.selectedProjects),
                        status: this.bulkEditStatusControl.value.id,
                        updateTasks: updateTasks,
                        mandateManagementTaskIds,
                        closingSectionIds,
                    }),
                );
            }
            this.clearAllSelections();
            this.bulkEditStatusControl.reset();
        }
    }

    // cleanup: Remove selections for projects that are no longer visible after filtering
    private cleanupInvalidSelections(): void {
        // Get current visible project IDs
        const currentProjectIds = new Set<string>();
        this.projectsOverviewTableData.forEach((project) => {
            currentProjectIds.add(project.projectId);
        });

        // Count selections before cleanup for logging
        const initialProjectSelections = this.selectedProjects.size;

        // Remove project selections that are no longer visible
        this.selectedProjects.forEach((projectId) => {
            if (!currentProjectIds.has(projectId)) {
                this.selectedProjects.delete(projectId);
            }
        });

        // Update allProjectsSelected flag - if any selections were removed or if the number of visible projects changed, it can't be "all selected"
        if (
            this.selectedProjects.size < initialProjectSelections ||
            this.selectedProjects.size !== this.projectsOverviewTableData.length
        ) {
            this.allProjectsSelected = false;
        }

        // If all visible projects are selected after cleanup, update the allProjectsSelected flag
        if (
            this.projectsOverviewTableData.length > 0 &&
            this.selectedProjects.size === this.projectsOverviewTableData.length
        ) {
            this.allProjectsSelected = true;
        }
    }
}
